
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LazyLoadingFallback } from '@/components/lazy';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

const DashboardContainer = lazy(() => import('@/pages/dashboard/DashboardContainer'));
const Sessions = lazy(() => import('@/pages/Sessions'));
const History = lazy(() => import('@/pages/History'));
const Profile = lazy(() => import('@/pages/Profile'));
const CheckIns = lazy(() => import('@/pages/CheckIns'));
const PrivacySettings = lazy(() => import('@/pages/PrivacySettings'));
const Admin = lazy(() => import('@/pages/Admin'));
const SessionDetail = lazy(() => import('@/pages/SessionDetail'));
const SessionAnalytics = lazy(() => import('@/pages/SessionAnalytics'));
const HRVBoosterControl = lazy(() => import('./pages/HRVBoosterControl'));
const HRVMeasurements = lazy(() => import('@/pages/HRVMeasurements'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const PasswordReset = lazy(() => import('@/pages/PasswordReset'));
const Security = lazy(() => import('@/pages/Security'));
const Subscription = lazy(() => import('@/pages/subscription/Subscription'));
const Checkout = lazy(() => import('@/pages/subscription/Checkout'));
const ThankYou = lazy(() => import('@/pages/subscription/ThankYou'));
const AddSessions = lazy(() => import('@/pages/subscription/AddSessions'));
const PurchaseHistory = lazy(() => import('@/pages/subscription/PurchaseHistory'));
const OAuthCallback = lazy(() => import('@/pages/workout/OAuthCallback'));
const ConnectHeartRate = lazy(() => import('@/pages/ConnectHeartRate'));

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading dashboard..." />}>
          <DashboardContainer />
        </Suspense>
      } />
      <Route path="/sessions" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading sessions..." />}>
          <Sessions />
        </Suspense>
      } />
      <Route path="/history" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading history..." />}>
          <History />
        </Suspense>
      } />
      <Route path="/profile" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading profile..." />}>
          <Profile />
        </Suspense>
      } />
      <Route path="/check-ins" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading check-ins..." />}>
          <CheckIns />
        </Suspense>
      } />
      <Route path="/privacy-settings" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading privacy settings..." />}>
          <PrivacySettings />
        </Suspense>
      } />
      <Route path="/auth/callback" element={
        <Suspense fallback={<LazyLoadingFallback message="Processing authentication..." />}>
          <AuthCallback />
        </Suspense>
      } />
      <Route path="/reset-password" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading reset password..." />}>
          <ResetPassword />
        </Suspense>
      } />
      <Route path="/password-reset" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading password reset..." />}>
          <PasswordReset />
        </Suspense>
      } />
      <Route path="/security" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading security..." />}>
          <Security />
        </Suspense>
      } />
      <Route path="/workout/oauth/callback" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading OAuth callback..." />}>
          <OAuthCallback />
        </Suspense>
      } />
      
      {/* HRV Booster Control route */}
      <Route path="/hrv-control" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading HRV control..." />}>
          <HRVBoosterControl />
        </Suspense>
      } />
      {/* Heart Rate Monitor Connection route */}
      <Route path="/connect-heart-rate" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading device connection..." />}>
          <ConnectHeartRate />
        </Suspense>
      } />
      {/* HRV Measurements route */}
      <Route path="/hrv-measurements" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading HRV measurements..." />}>
          <HRVMeasurements />
        </Suspense>
      } />
      
      {/* Session routes */}
      <Route path="/session/:sessionId" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading session..." />}>
          <SessionDetail />
        </Suspense>
      } />
      {/* Backward-compatibility: support legacy /sessions/:sessionId links */}
      <Route path="/sessions/:sessionId" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading session..." />}>
          <SessionDetail />
        </Suspense>
      } />
      {/* Redirect bare /session to sessions list to avoid 404 from breadcrumbs */}
      <Route path="/session" element={<Navigate to="/sessions" replace />} />
      <Route path="/session/:sessionId/analytics" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading analytics..." />}>
          <SessionAnalytics />
        </Suspense>
      } />
      
      {/* Subscription routes */}
      <Route path="/subscription" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading subscription..." />}>
          <Subscription />
        </Suspense>
      } />
      <Route path="/subscription/checkout" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading checkout..." />}>
          <Checkout />
        </Suspense>
      } />
      <Route path="/subscription/thank-you" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading thank you..." />}>
          <ThankYou />
        </Suspense>
      } />
      <Route path="/subscription/add-sessions" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading add sessions..." />}>
          <AddSessions />
        </Suspense>
      } />
      <Route path="/subscription/history" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading purchase history..." />}>
          <PurchaseHistory />
        </Suspense>
      } />
      
      {/* Admin routes - Protected */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LazyLoadingFallback message="Loading admin..." />}>
              <Admin />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* 404 route */}
      <Route path="*" element={
        <Suspense fallback={<LazyLoadingFallback message="Loading..." />}>
          <NotFound />
        </Suspense>
      } />
    </Routes>
  );
};

export default AppRoutes;
